/**
 * Document Generation template admin: register and unregister docgen templates.
 * Visible only to users with templates.manage (e.g. Doc Gen specialist).
 */
import React, { useState, useEffect, useLayoutEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import ContentHeader from '../components/ContentHeader';
import { templateAdminAPI, clientDocumentAPI } from '../services/api';
import { formatDisplayDate } from '../utils/dateFormat';

const TEMPLATE_MENU_WIDTH = 180;

function TemplateActionsMenu({ menu, onClose, onEdit, onDownload, onToggleEnabled, onDelete }) {
  const [position, setPosition] = useState({ top: 0, left: 0 });

  useLayoutEffect(() => {
    if (!menu?.rect) return;
    const { rect } = menu;
    let left = rect.right - TEMPLATE_MENU_WIDTH;
    if (left < 8) left = 8;
    let top = rect.bottom + 6;
    if (top + 200 > window.innerHeight - 8 && rect.top > 212) {
      top = Math.max(8, rect.top - 200 - 6);
    }
    setPosition({ top, left });
  }, [menu]);

  if (!menu?.template) return null;
  const t = menu.template;

  const run = (fn) => () => { onClose(); fn(t); };

  return createPortal(
    <>
      <div className="file-actions-menu-backdrop" onClick={onClose} aria-hidden="true" />
      <div
        role="menu"
        className="file-actions-menu-dropdown-portal"
        style={{ position: 'fixed', top: position.top, left: position.left, width: TEMPLATE_MENU_WIDTH, zIndex: 1060 }}
      >
        <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onEdit)}>Edit details</button>
        <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(onDownload)}>Download file</button>
        <div className="file-actions-menu-separator" />
        {t.enabled
          ? <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(() => onToggleEnabled(t.id, false))}>Hide from picker</button>
          : <button type="button" role="menuitem" className="file-actions-menu-item" onClick={run(() => onToggleEnabled(t.id, true))}>Show in picker</button>
        }
        <div className="file-actions-menu-separator" />
        <button type="button" role="menuitem" className="file-actions-menu-item file-actions-menu-item-danger" onClick={run(onDelete)}>Unregister</button>
      </div>
    </>,
    document.body
  );
}
import './DocGenTemplatesAdmin.css';

/** Flatten folder tree to { key, label } for dropdown (matches TenantSettings / DocGenLanding). */
function flattenFolderOptions(folders, prefix = '') {
  if (!folders || !folders.length) return [];
  const out = [];
  for (const f of folders) {
    const label = prefix ? `${prefix} → ${f.name}` : (f.name || f.key || '');
    const key = (f.key && String(f.key).trim()) || (f.name && f.name.replace(/\s+/g, '-').toLowerCase()) || '';
    if (key) out.push({ key, label });
    if (f.children?.length) out.push(...flattenFolderOptions(f.children, f.name || f.key));
  }
  return out;
}

const DOCGEN_CATEGORIES = [
  { value: 'Client', label: 'Client (one document for this client)' },
  { value: 'MonthlyContact', label: 'Monthly contact email' },
  { value: 'ASQ', label: 'ASQ & Social History' },
  { value: 'FaxScript', label: 'Fax scripts (Doctor / CBO)' },
  { value: 'TPC', label: 'TPC (Transition Planning Conference)' },
  { value: 'ATPacket', label: 'AT Requests (AT packet documents)' },
  { value: 'DataCorrection', label: 'Data correction letters (Main / Supplemental picker)' },
  { value: 'Other', label: 'Other communications' },
];

const PROMPT_TAG_OPTIONS = [
  { value: 'INTAKE_DATE', label: 'Intake date' },
  { value: 'TPC_DATE', label: 'TPC date' },
  { value: 'providerId', label: 'Doctor (provider)' },
  { value: 'dataCorrection', label: 'Data correction letter (picker on Data Corrections edit)' },
];

function DocGenTemplatesAdmin() {
  const { tenantPath } = useTenantFromUrl();
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [registerCategory, setRegisterCategory] = useState('Client');
  const [registerLanguage, setRegisterLanguage] = useState('EN');
  const [registerMinAge, setRegisterMinAge] = useState('');
  const [registerMaxAge, setRegisterMaxAge] = useState('');
  const [registerPromptTags, setRegisterPromptTags] = useState([]);
  const [showRegisterOptions, setShowRegisterOptions] = useState(false);
  const [registering, setRegistering] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [templateMenu, setTemplateMenu] = useState(null);
  const [editDisplayName, setEditDisplayName] = useState('');
  const [editMinAge, setEditMinAge] = useState('');
  const [editMaxAge, setEditMaxAge] = useState('');
  const [editPromptTags, setEditPromptTags] = useState([]);
  const [editDefaultFolderKey, setEditDefaultFolderKey] = useState('');
  const [savingId, setSavingId] = useState(null);
  const [tagRefList, setTagRefList] = useState([]);
  const [tagRefLoading, setTagRefLoading] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const showDocGenFieldsView = location.pathname.includes('doc-gen-fields');
  const [editModalTemplate, setEditModalTemplate] = useState(null);
  const [editModalDisplayName, setEditModalDisplayName] = useState('');
  const [editModalCategory, setEditModalCategory] = useState('Client');
  const [editModalLanguage, setEditModalLanguage] = useState('EN');
  const [editModalMinAge, setEditModalMinAge] = useState('');
  const [editModalMaxAge, setEditModalMaxAge] = useState('');
  const [editModalPromptTags, setEditModalPromptTags] = useState([]);
  const [editModalSaving, setEditModalSaving] = useState(false);
  const [filterCategory, setFilterCategory] = useState('');
  const [filterLanguage, setFilterLanguage] = useState('');
  const [filterType, setFilterType] = useState('');
  const [filterNameSearch, setFilterNameSearch] = useState('');
  const [folderOptions, setFolderOptions] = useState([]);
  const [registerDefaultFolderKey, setRegisterDefaultFolderKey] = useState('');
  const [editModalDefaultFolderKey, setEditModalDefaultFolderKey] = useState('');

  const filteredTemplates = useMemo(() => {
    const q = filterNameSearch.trim().toLowerCase();
    return templates.filter((t) => {
      if (q) {
        const display = String(t.displayName || '').toLowerCase();
        const file = String(t.fileName || '').toLowerCase();
        const code = String(t.templateCode || '').toLowerCase();
        if (!display.includes(q) && !file.includes(q) && !code.includes(q)) return false;
      }
      if (filterCategory && t.category !== filterCategory) return false;
      if (filterLanguage && (t.language || 'EN') !== filterLanguage) return false;
      if (filterType) {
        const type = (t.fileName || '').toLowerCase().endsWith('.pdf') ? 'PDF' : 'DOCX';
        if (type !== filterType) return false;
      }
      return true;
    });
  }, [templates, filterNameSearch, filterCategory, filterLanguage, filterType]);

  useEffect(() => {
    loadTemplates();
  }, []);

  // Load client folder template for default-folder dropdown (register + edit)
  useEffect(() => {
    let cancelled = false;
    clientDocumentAPI.getTemplate()
      .then((res) => {
        if (cancelled) return;
        const template = res.data?.data;
        const folders = template?.template_structure?.folders ?? [];
        setFolderOptions(flattenFolderOptions(folders));
      })
      .catch(() => {
        if (!cancelled) setFolderOptions([]);
      });
    return () => { cancelled = true; };
  }, []);

  const loadTemplates = async (options = {}) => {
    const keepListOnError = options.keepListOnError === true;
    try {
      setLoading(true);
      setError(null);
      const res = await templateAdminAPI.listAdmin();
      setTemplates(res.data?.templates || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load templates');
      if (!keepListOnError) setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files || []);
    const allowed = files.filter((f) => {
      const ext = (f.name || '').toLowerCase();
      return ext.endsWith('.docx') || ext.endsWith('.pdf');
    });
    setSelectedFiles(allowed);
    if (allowed.length > 0) setShowRegisterOptions(true);
    if (allowed.length !== files.length) setMessage('Only .docx and .pdf files are used.');
    else setMessage(null);
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      setMessage('Select one or more .docx or .pdf files to register.');
      return;
    }
    try {
      setRegistering(true);
      setMessage(null);
      const options = { language: registerLanguage };
      if (registerMinAge !== '') options.minAgeMonths = registerMinAge;
      if (registerMaxAge !== '') options.maxAgeMonths = registerMaxAge;
      if (registerPromptTags.length > 0) options.promptTags = registerPromptTags;
      if (registerDefaultFolderKey) options.defaultFolderKey = registerDefaultFolderKey;
      const res = await templateAdminAPI.register(selectedFiles, registerCategory, options);
      const data = res.data || {};
      const registered = data.registered || [];
      const errors = data.errors || [];
      if (registered.length) {
        setMessage('Registered ' + registered.length + ' template(s).');
        setSelectedFiles([]);
        setRegisterMinAge('');
        setRegisterMaxAge('');
        setRegisterPromptTags([]);
        setRegisterDefaultFolderKey('');
        const el = document.getElementById('docgen-admin-file-input');
        if (el && el.form) el.form.reset();
        loadTemplates();
      }
      if (errors.length) {
        setMessage((m) => (m ? m + ' ' : '') + 'Errors: ' + errors.map((x) => x.file + ': ' + x.error).join('; '));
      }
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Failed to register templates');
    } finally {
      setRegistering(false);
    }
  };

  const handleSetEnabled = async (id, enabled) => {
    try {
      setTogglingId(id);
      await templateAdminAPI.setEnabled(id, enabled);
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, enabled } : t)));
      setMessage(enabled ? 'Template visible in picker.' : 'Template hidden.');
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Failed to update template');
    } finally {
      setTogglingId(null);
    }
  };

  const handleDelete = async (t) => {
    if (!window.confirm('Unregister this template? The template file is kept in storage but will no longer appear in any document picker. You can re-register it later by uploading again. Continue?')) return;
    try {
      await templateAdminAPI.deleteTemplate(t.id);
      setTemplates((prev) => prev.filter((x) => x.id !== t.id));
      setMessage('Template removed from the system.');
      if (expandedId === t.id) setExpandedId(null);
      if (editingId === t.id) setEditingId(null);
      if (editModalTemplate?.id === t.id) setEditModalTemplate(null);
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Delete failed');
    }
  };

  const handleDownload = async (t) => {
    try {
      const res = await templateAdminAPI.download(t.id);
      const blob = res.data;
      const disposition = res.headers?.['content-disposition'] || '';
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const fileName = (match && match[1]) ? match[1].trim() : (t.fileName || `template_${t.id}.docx`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
      setMessage(`Downloaded ${fileName}`);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const j = JSON.parse(text);
          setMessage(j.error || 'Download failed');
        } catch {
          setMessage('Download failed');
        }
      } else {
        setMessage(err.response?.data?.error || err.message || 'Download failed');
      }
    }
  };

  const toggleExpanded = (id) => {
    setExpandedId((prev) => (prev === id ? null : id));
    if (editingId === id) {
      setEditingId(null);
      setEditDisplayName('');
      setEditMinAge('');
      setEditMaxAge('');
      setEditPromptTags([]);
      setEditDefaultFolderKey('');
    }
  };

  const openEdit = (e, t) => {
    e.stopPropagation();
    setExpandedId(t.id);
    setEditingId(t.id);
    setEditDisplayName(t.displayName || '');
    setEditMinAge(t.minAgeMonths != null ? String(t.minAgeMonths) : '');
    setEditMaxAge(t.maxAgeMonths != null ? String(t.maxAgeMonths) : '');
    setEditPromptTags(Array.isArray(t.promptTags) ? [...t.promptTags] : []);
    setEditDefaultFolderKey(t.defaultFolderKey || '');
  };

  const cancelEdit = (e) => {
    if (e) e.stopPropagation();
    setEditingId(null);
    setEditDisplayName('');
    setEditMinAge('');
    setEditMaxAge('');
    setEditPromptTags([]);
    setEditDefaultFolderKey('');
  };

  const handleSaveDetails = async (id) => {
    const trimmedName = (editDisplayName || '').trim();
    if (!trimmedName) {
      setMessage('Display name is required.');
      return;
    }
    try {
      setSavingId(id);
      const payload = {
        displayName: trimmedName,
        minAgeMonths: editMinAge === '' ? null : parseInt(editMinAge, 10),
        maxAgeMonths: editMaxAge === '' ? null : parseInt(editMaxAge, 10),
        promptTags: editPromptTags,
        defaultFolderKey: editDefaultFolderKey || null,
      };
      if (Number.isNaN(payload.minAgeMonths)) payload.minAgeMonths = null;
      if (Number.isNaN(payload.maxAgeMonths)) payload.maxAgeMonths = null;
      await templateAdminAPI.updateTemplate(id, payload);
      setTemplates((prev) => prev.map((t) => (t.id === id ? { ...t, displayName: trimmedName, minAgeMonths: payload.minAgeMonths, maxAgeMonths: payload.maxAgeMonths, promptTags: payload.promptTags, defaultFolderKey: payload.defaultFolderKey } : t)));
      setMessage('Template details updated.');
      cancelEdit();
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Failed to update template');
    } finally {
      setSavingId(null);
    }
  };

  const toggleRegisterPromptTag = (value) => {
    setRegisterPromptTags((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const toggleEditPromptTag = (value) => {
    setEditPromptTags((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  useEffect(() => {
    if (showDocGenFieldsView && tagRefList.length === 0 && !tagRefLoading) {
      setTagRefLoading(true);
      templateAdminAPI.getTagReference()
        .then((res) => setTagRefList(res.data?.tags || []))
        .catch(() => setTagRefList([]))
        .finally(() => setTagRefLoading(false));
    }
  }, [showDocGenFieldsView]);

  const escapeCsvCell = (val) => {
    const s = String(val ?? '');
    if (s.includes(',') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
    return s;
  };

  const handleTagRefDownload = () => {
    const header = 'Tag,Description,Source';
    const rows = (tagRefList || []).map((r) => [r.tag, r.description, r.source].map(escapeCsvCell).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'doc-gen-fields-reference.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleTagRefPrint = () => {
    const title = 'Doc Gen Fields';
    const intro = 'Use these tags in Word content controls or PDF form fields. When you generate a document, each tag is replaced with the value described below. For client provider team fields (Edit Client → Provider team; Pro1…Pro9 pattern rows), N means digit 1–9 (e.g. Pro3Phone). Same catalog as Settings → Email Templates → Email merge fields; in email templates wrap each tag in double braces (e.g. {{PRO_TEAM_EMAIL}}).';
    const rows = (tagRefList || []).map((r) => `<tr><td><code>${escapeHtml(r.tag)}</code></td><td>${escapeHtml(r.description)}</td><td>${escapeHtml(r.source)}</td></tr>`).join('');
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${escapeHtml(title)}</title><style>
      body{font-family:system-ui,sans-serif;padding:1.5rem;font-size:0.9rem;}
      h1{margin:0 0 0.5rem;font-size:1.25rem;}
      p{color:#495057;margin:0 0 1rem;}
      table{border-collapse:collapse;width:100%;}
      th,td{padding:0.4rem 0.6rem;text-align:left;border-bottom:1px solid #eee;}
      th{background:#f8f9fa;font-weight:600;}
      code{background:#f1f3f5;padding:0.15rem 0.35rem;border-radius:4px;font-size:0.85em;}
    </style></head><body><h1>${escapeHtml(title)}</h1><p>${escapeHtml(intro)}</p><table><thead><tr><th>Tag</th><th>Description</th><th>Source</th></tr></thead><tbody>${rows}</tbody></table></body></html>`;
    const w = window.open('', '_blank');
    w.document.write(html);
    w.document.close();
    w.focus();
    setTimeout(() => { w.print(); w.close(); }, 250);
  };

  const escapeHtml = (s) => {
    const div = document.createElement('div');
    div.textContent = s ?? '';
    return div.innerHTML;
  };

  const openEditModal = (t) => {
    setEditModalTemplate(t);
    setEditModalDisplayName(t.displayName || '');
    setEditModalCategory(t.category || 'Client');
    setEditModalLanguage(t.language === 'ES' ? 'ES' : 'EN');
    setEditModalMinAge(t.minAgeMonths != null ? String(t.minAgeMonths) : '');
    setEditModalMaxAge(t.maxAgeMonths != null ? String(t.maxAgeMonths) : '');
    setEditModalPromptTags(Array.isArray(t.promptTags) ? [...t.promptTags] : []);
    setEditModalDefaultFolderKey(t.defaultFolderKey || '');
  };

  const closeEditModal = () => {
    setEditModalTemplate(null);
    setEditModalSaving(false);
  };

  const toggleEditModalPromptTag = (value) => {
    setEditModalPromptTags((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const handleSaveEditModal = async () => {
    const t = editModalTemplate;
    if (!t || !editModalDisplayName.trim()) {
      setMessage('Display name is required.');
      return;
    }
    try {
      setEditModalSaving(true);
      setMessage(null);
      const payload = {
        displayName: editModalDisplayName.trim(),
        category: editModalCategory,
        language: editModalLanguage,
        minAgeMonths: editModalMinAge === '' ? null : parseInt(editModalMinAge, 10),
        maxAgeMonths: editModalMaxAge === '' ? null : parseInt(editModalMaxAge, 10),
        promptTags: editModalPromptTags,
        defaultFolderKey: editModalDefaultFolderKey || null,
      };
      if (Number.isNaN(payload.minAgeMonths)) payload.minAgeMonths = null;
      if (Number.isNaN(payload.maxAgeMonths)) payload.maxAgeMonths = null;
      const res = await templateAdminAPI.updateTemplate(t.id, payload);
      const updated = res.data?.template;
      if (updated) {
        setTemplates((prev) => prev.map((x) => (x.id === t.id ? { ...x, ...updated } : x)));
      }
      setMessage('Template updated.');
      closeEditModal();
      await loadTemplates({ keepListOnError: true });
    } catch (err) {
      setMessage(err.response?.data?.error || err.message || 'Failed to update template');
    } finally {
      setEditModalSaving(false);
    }
  };

  if (showDocGenFieldsView) {
    return (
      <div className="docgen-admin-page">
        <ContentHeader
          title="Doc Gen Fields"
          actions={
            <div className="docgen-admin-tag-ref-header-actions">
              <button type="button" className="docgen-admin-btn docgen-admin-btn-primary docgen-admin-tag-ref-action-btn" onClick={handleTagRefPrint} disabled={tagRefLoading || tagRefList.length === 0}>
                Print
              </button>
              <button type="button" className="docgen-admin-btn docgen-admin-btn-primary docgen-admin-tag-ref-action-btn" onClick={handleTagRefDownload} disabled={tagRefLoading || tagRefList.length === 0}>
                Download
              </button>
            </div>
          }
        />
        <p className="docgen-admin-tag-ref-intro docgen-admin-tag-ref-intro-page">
          Use these tags in Word content controls or PDF form fields. When you generate a document, each tag is replaced with the value described below. For the <strong>client provider team</strong> (Edit Client → Provider team), pattern rows use N as digit 1–9 (e.g. Pro3Phone for the third provider on the team). The same tag names, descriptions, and sources appear under <strong>Settings → Email Templates → Email merge fields</strong>; there each tag is wrapped in double braces (for example <code>{'{{PRO_TEAM_EMAIL}}'}</code>) in subject, body, To, and CC.
        </p>
        {tagRefLoading ? (
          <p className="docgen-admin-tag-ref-loading">Loading…</p>
        ) : (
          <div className="docgen-admin-tag-ref-table-wrap docgen-admin-tag-ref-table-wrap-page">
            <table className="docgen-admin-tag-ref-table">
              <thead>
                <tr>
                  <th>Tag</th>
                  <th>Description</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {tagRefList.map((row, idx) => (
                  <tr key={`${row.tag}-${idx}`}>
                    <td className="docgen-admin-tag-ref-tag"><code>{row.tag}</code></td>
                    <td>{row.description}</td>
                    <td>{row.source}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="docgen-admin-page">
      <ContentHeader
        title="Document Templates"
        titleHint="Register document templates so they appear on the Documents page by category. Visible = shown in the picker; Hidden = not shown (template stays in the system). Use Download to edit outside the system, then upload again to replace. Delete removes the template and file from the system."
      />
      {message && <div className="docgen-admin-message" role="alert">{message}</div>}
      {error && <div className="docgen-admin-error" role="alert">{error}</div>}

      <section className="docgen-admin-section docgen-admin-register">
        <div className="docgen-admin-register-inner">
        <div className="docgen-admin-register-header">
          <h2 className="docgen-admin-section-title">Register Templates</h2>
          <button type="button" className="docgen-admin-btn docgen-admin-btn-primary" onClick={() => navigate(tenantPath('/settings/document-templates/doc-gen-fields'))}>
            Doc Gen Fields
          </button>
        </div>
        <form onSubmit={handleRegister} className="docgen-admin-form">
          <div className="docgen-admin-form-row">
            <label className="docgen-admin-file-row-label">.docx or .pdf file(s)</label>
            <div className="docgen-admin-file-row">
              <span className="docgen-admin-file-status">
                {selectedFiles.length === 0
                  ? 'No files chosen'
                  : selectedFiles.length === 1
                    ? selectedFiles[0].name
                    : selectedFiles.length <= 3
                      ? `${selectedFiles.length} files: ${selectedFiles.map((f) => f.name).join(', ')}`
                      : `${selectedFiles.length} files selected`}
              </span>
              <input
                id="docgen-admin-file-input"
                type="file"
                accept=".docx,.pdf"
                multiple
                onChange={handleFileChange}
                className="docgen-admin-file-input-hidden"
                aria-label="Choose .docx or .pdf files"
              />
              <label htmlFor="docgen-admin-file-input" className="docgen-admin-btn docgen-admin-btn-primary docgen-admin-choose-files-btn">
                Choose files
              </label>
            </div>
          </div>
          {selectedFiles.length > 0 && (
            <>
          <div className="docgen-admin-form-row">
            <label htmlFor="docgen-admin-category">Category</label>
            <select
              id="docgen-admin-category"
              value={registerCategory}
              onChange={(e) => setRegisterCategory(e.target.value)}
              className="docgen-admin-select"
            >
              {DOCGEN_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{c.value}</option>
              ))}
            </select>
          </div>
          <div className="docgen-admin-form-row">
            <label htmlFor="docgen-admin-language">Template Language</label>
            <select
              id="docgen-admin-language"
              value={registerLanguage}
              onChange={(e) => setRegisterLanguage(e.target.value)}
              className="docgen-admin-select"
              required
            >
              <option value="EN">English</option>
              <option value="ES">Spanish</option>
            </select>
          </div>
          <div className="docgen-admin-form-row">
            <label htmlFor="docgen-admin-default-folder">Default client folder location</label>
            <select
              id="docgen-admin-default-folder"
              value={registerDefaultFolderKey}
              onChange={(e) => setRegisterDefaultFolderKey(e.target.value)}
              className="docgen-admin-select"
              aria-describedby="docgen-admin-default-folder-hint"
            >
              <option value="">— None —</option>
              {folderOptions.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
            <span id="docgen-admin-default-folder-hint" className="docgen-admin-hint">Optional. When saving a generated document to the client folder, this folder is pre-selected.</span>
          </div>
          <div className="docgen-admin-form-row">
            <button type="button" className="docgen-admin-options-toggle" onClick={() => setShowRegisterOptions((v) => !v)} aria-expanded={showRegisterOptions}>
              {showRegisterOptions ? '−' : '+'} Age Range and Prompt Fields (optional)
            </button>
            {showRegisterOptions && (
              <div className="docgen-admin-options-panel">
                <p className="docgen-admin-options-hint">Applied to all files in this batch. Use for ASQ (e.g. 9–14 months = 12mo form) or any template that needs intake date / doctor.</p>
                <div className="docgen-admin-options-grid">
                  <div>
                    <label htmlFor="docgen-register-min-age">Min age (months)</label>
                    <input
                      id="docgen-register-min-age"
                      type="number"
                      min={0}
                      className="docgen-admin-input docgen-admin-input-narrow"
                      value={registerMinAge}
                      onChange={(e) => setRegisterMinAge(e.target.value)}
                      placeholder="—"
                    />
                  </div>
                  <div>
                    <label htmlFor="docgen-register-max-age">Max age (months)</label>
                    <input
                      id="docgen-register-max-age"
                      type="number"
                      min={0}
                      className="docgen-admin-input docgen-admin-input-narrow"
                      value={registerMaxAge}
                      onChange={(e) => setRegisterMaxAge(e.target.value)}
                      placeholder="—"
                    />
                  </div>
                </div>
                <div className="docgen-admin-form-row">
                  <span className="docgen-admin-options-label">Prompt fields</span>
                  <div className="docgen-admin-checkbox-group">
                    {PROMPT_TAG_OPTIONS.map((opt) => (
                      <label key={opt.value} className="docgen-admin-checkbox-label">
                        <input type="checkbox" checked={registerPromptTags.includes(opt.value)} onChange={() => toggleRegisterPromptTag(opt.value)} />
                        {opt.label}
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
            </>
          )}
          <div className="docgen-admin-form-actions">
            <button type="submit" className="docgen-admin-btn docgen-admin-btn-primary" disabled={registering || selectedFiles.length === 0}>
              {registering ? 'Registering…' : 'Register'}
            </button>
          </div>
        </form>
        </div>
      </section>

      <section className="docgen-admin-section docgen-admin-list">
        <h2 className="docgen-admin-section-title">Registered Templates</h2>
        {loading ? (
          <p className="docgen-admin-loading">Loading…</p>
        ) : templates.length === 0 ? (
          <p className="docgen-admin-empty">No document templates registered yet. Register .docx or .pdf files above.</p>
        ) : filteredTemplates.length === 0 ? (
          <p className="docgen-admin-empty">No templates match your search or filters.</p>
        ) : (
          <div className="docgen-admin-table-wrap">
            <table className="docgen-admin-table">
              <thead>
                {templates.length > 0 && (
                  <tr className="docgen-admin-filter-row">
                    <th className="docgen-admin-filter-cell docgen-admin-filter-name-cell docgen-admin-col-display-name">
                      <input
                        id="docgen-filter-name"
                        type="search"
                        value={filterNameSearch}
                        onChange={(e) => setFilterNameSearch(e.target.value)}
                        placeholder="Search name…"
                        className="docgen-admin-filter-name-input"
                        aria-label="Search by display name, file name, or template code"
                      />
                    </th>
                    <th className="docgen-admin-filter-cell docgen-admin-col-category">
                      <select id="docgen-filter-category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} className="docgen-admin-filter-select" aria-label="Filter by category">
                        <option value="">All</option>
                        {DOCGEN_CATEGORIES.map((c) => (
                          <option key={c.value} value={c.value}>{c.value}</option>
                        ))}
                      </select>
                    </th>
                    <th className="docgen-admin-filter-cell">
                      <select id="docgen-filter-language" value={filterLanguage} onChange={(e) => setFilterLanguage(e.target.value)} className="docgen-admin-filter-select" aria-label="Filter by language">
                        <option value="">All</option>
                        <option value="EN">English</option>
                        <option value="ES">Spanish</option>
                      </select>
                    </th>
                    <th className="docgen-admin-filter-cell">
                      <select id="docgen-filter-type" value={filterType} onChange={(e) => setFilterType(e.target.value)} className="docgen-admin-filter-select" aria-label="Filter by type">
                        <option value="">All</option>
                        <option value="DOCX">DOCX</option>
                        <option value="PDF">PDF</option>
                      </select>
                    </th>
                    <th className="docgen-admin-filter-cell" />
                    <th className="docgen-admin-filter-cell" />
                  </tr>
                )}
                <tr>
                  <th className="docgen-admin-col-display-name">Display name</th>
                  <th className="docgen-admin-col-category">Category</th>
                  <th>Language</th>
                  <th>Type</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredTemplates.map((t) => (
                  <React.Fragment key={t.id}>
                    <tr
                      className={`docgen-admin-row ${expandedId === t.id ? 'docgen-admin-row-expanded' : ''}`}
                      onClick={() => toggleExpanded(t.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleExpanded(t.id); } }}
                      aria-expanded={expandedId === t.id}
                    >
                      <td className="docgen-admin-col-display-name">{t.displayName}</td>
                      <td className="docgen-admin-col-category" title={t.category}>{t.category}</td>
                      <td>{t.language === 'ES' ? 'Spanish' : 'English'}</td>
                      <td className="docgen-admin-type-cell">
                        {(t.fileName || '').toLowerCase().endsWith('.pdf') ? 'PDF' : 'DOCX'}
                      </td>
                      <td>
                        <span className={t.enabled ? 'docgen-admin-status-on' : 'docgen-admin-status-off'}>
                          {t.enabled ? 'Visible' : 'Hidden'}
                        </span>
                      </td>
                      <td onClick={(e) => e.stopPropagation()} className="docgen-admin-actions-cell">
                        <div className="file-actions-menu-root">
                          <button
                            type="button"
                            className="file-actions-menu-trigger"
                            aria-label={`More actions for ${t.displayName || t.fileName}`}
                            aria-haspopup="menu"
                            aria-expanded={templateMenu?.template?.id === t.id}
                            onClick={(e) => {
                              e.stopPropagation();
                              const rect = e.currentTarget.getBoundingClientRect();
                              setTemplateMenu((prev) => prev?.template?.id === t.id ? null : { template: t, rect });
                            }}
                          >
                            <span aria-hidden="true" className="file-actions-menu-trigger-dots">⋯</span>
                          </button>
                        </div>
                      </td>
                    </tr>
                    {expandedId === t.id && (
                      <tr className="docgen-admin-detail-row">
                        <td colSpan={6}>
                          <div className="docgen-admin-detail-panel" onClick={(e) => e.stopPropagation()}>
                            {editingId === t.id ? (
                              <>
                                <div className="docgen-admin-form-row">
                                  <label htmlFor={`docgen-inline-display-name-${t.id}`}>Display name</label>
                                  <input
                                    id={`docgen-inline-display-name-${t.id}`}
                                    type="text"
                                    className="docgen-admin-input"
                                    value={editDisplayName}
                                    onChange={(e) => setEditDisplayName(e.target.value)}
                                    placeholder="Template display name"
                                  />
                                </div>
                                <div className="docgen-admin-form-row">
                                  <label htmlFor={`docgen-inline-default-folder-${t.id}`}>Default client folder location</label>
                                  <select
                                    id={`docgen-inline-default-folder-${t.id}`}
                                    value={editDefaultFolderKey}
                                    onChange={(e) => setEditDefaultFolderKey(e.target.value)}
                                    className="docgen-admin-select"
                                  >
                                    <option value="">— None —</option>
                                    {folderOptions.map((opt) => (
                                      <option key={opt.key} value={opt.key}>{opt.label}</option>
                                    ))}
                                  </select>
                                </div>
                                <div className="docgen-admin-detail-grid">
                                  <div>
                                    <label>Min age (months)</label>
                                    <input type="number" min={0} className="docgen-admin-input docgen-admin-input-narrow" value={editMinAge} onChange={(e) => setEditMinAge(e.target.value)} placeholder="—" />
                                  </div>
                                  <div>
                                    <label>Max age (months)</label>
                                    <input type="number" min={0} className="docgen-admin-input docgen-admin-input-narrow" value={editMaxAge} onChange={(e) => setEditMaxAge(e.target.value)} placeholder="—" />
                                  </div>
                                </div>
                                <div className="docgen-admin-detail-tags">
                                  <span className="docgen-admin-options-label">Prompt fields</span>
                                  <div className="docgen-admin-checkbox-group">
                                    {PROMPT_TAG_OPTIONS.map((opt) => (
                                      <label key={opt.value} className="docgen-admin-checkbox-label">
                                        <input type="checkbox" checked={editPromptTags.includes(opt.value)} onChange={() => toggleEditPromptTag(opt.value)} />
                                        {opt.label}
                                      </label>
                                    ))}
                                  </div>
                                </div>
                                <div className="docgen-admin-detail-actions">
                                  <button type="button" className="docgen-admin-btn docgen-admin-btn-primary" disabled={savingId === t.id} onClick={() => handleSaveDetails(t.id)}>
                                    {savingId === t.id ? 'Saving…' : 'Save'}
                                  </button>
                                  <button type="button" className="docgen-admin-btn docgen-admin-btn-secondary" onClick={cancelEdit}>Cancel</button>
                                </div>
                              </>
                            ) : (
                              <>
                                <dl className="docgen-admin-detail-dl">
                                  <dt>File name</dt>
                                  <dd>{t.fileName}</dd>
                                  <dt>Language</dt>
                                  <dd>{t.language === 'ES' ? 'Spanish' : 'English'}</dd>
                                  <dt>Default client folder</dt>
                                  <dd>{t.defaultFolderKey ? (folderOptions.find((o) => o.key === t.defaultFolderKey)?.label || t.defaultFolderKey) : '—'}</dd>
                                  <dt>Age range</dt>
                                  <dd>
                                    {t.minAgeMonths != null || t.maxAgeMonths != null
                                      ? `${t.minAgeMonths ?? '—'}–${t.maxAgeMonths ?? '—'} months`
                                      : '—'}
                                  </dd>
                                  <dt>Prompt fields</dt>
                                  <dd>{Array.isArray(t.promptTags) && t.promptTags.length > 0 ? t.promptTags.join(', ') : '—'}</dd>
                                  <dt>Uploaded by</dt>
                                  <dd>{t.createdByName ? `${t.createdByName}${t.createdAt ? ' on ' + formatDisplayDate(t.createdAt) : ''}` : '—'}</dd>
                                  <dt>Last changed by</dt>
                                  <dd>{t.updatedByName ? `${t.updatedByName}${t.updatedAt ? ' on ' + formatDisplayDate(t.updatedAt) : ''}` : '—'}</dd>
                                </dl>
                                <div className="docgen-admin-detail-actions">
                                  <button type="button" className="docgen-admin-btn docgen-admin-btn-primary" onClick={(e) => openEdit(e, t)}>Edit details</button>
                                </div>
                              </>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {editModalTemplate && (
        <div className="docgen-admin-modal-overlay" onClick={closeEditModal} role="dialog" aria-modal="true" aria-labelledby="docgen-edit-modal-title">
          <div className="docgen-admin-edit-modal docgen-admin-tag-ref-modal" onClick={(e) => e.stopPropagation()}>
            <div className="docgen-admin-tag-ref-header">
              <h2 id="docgen-edit-modal-title">Edit Template</h2>
              <button type="button" className="docgen-admin-tag-ref-close" onClick={closeEditModal} aria-label="Close">×</button>
            </div>
            <div className="docgen-admin-edit-modal-body">
              <p className="docgen-admin-edit-modal-file">{editModalTemplate.fileName}</p>
              <div className="docgen-admin-form-row">
                <label htmlFor="docgen-edit-display-name">Display name</label>
                <input
                  id="docgen-edit-display-name"
                  type="text"
                  className="docgen-admin-input"
                  value={editModalDisplayName}
                  onChange={(e) => setEditModalDisplayName(e.target.value)}
                  placeholder="Template display name"
                  autoFocus
                  aria-label="Display name"
                />
              </div>
              <div className="docgen-admin-form-row">
                <label htmlFor="docgen-edit-category">Category</label>
                <select
                  id="docgen-edit-category"
                  value={editModalCategory}
                  onChange={(e) => setEditModalCategory(e.target.value)}
                  className="docgen-admin-select"
                >
                  {DOCGEN_CATEGORIES.map((c) => (
                    <option key={c.value} value={c.value}>{c.value}</option>
                  ))}
                </select>
              </div>
              <div className="docgen-admin-form-row">
                <label htmlFor="docgen-edit-language">Template Language</label>
                <select
                  id="docgen-edit-language"
                  value={editModalLanguage}
                  onChange={(e) => setEditModalLanguage(e.target.value)}
                  className="docgen-admin-select"
                >
                  <option value="EN">English</option>
                  <option value="ES">Spanish</option>
                </select>
              </div>
              <div className="docgen-admin-form-row">
                <label htmlFor="docgen-edit-default-folder">Default client folder location</label>
                <select
                  id="docgen-edit-default-folder"
                  value={editModalDefaultFolderKey}
                  onChange={(e) => setEditModalDefaultFolderKey(e.target.value)}
                  className="docgen-admin-select"
                >
                  <option value="">— None —</option>
                  {folderOptions.map((opt) => (
                    <option key={opt.key} value={opt.key}>{opt.label}</option>
                  ))}
                </select>
              </div>
              <div className="docgen-admin-options-grid docgen-admin-edit-modal-ages">
                <div>
                  <label htmlFor="docgen-edit-min-age">Min age (months)</label>
                  <input
                    id="docgen-edit-min-age"
                    type="number"
                    min={0}
                    className="docgen-admin-input docgen-admin-input-narrow"
                    value={editModalMinAge}
                    onChange={(e) => setEditModalMinAge(e.target.value)}
                    placeholder="—"
                  />
                </div>
                <div>
                  <label htmlFor="docgen-edit-max-age">Max age (months)</label>
                  <input
                    id="docgen-edit-max-age"
                    type="number"
                    min={0}
                    className="docgen-admin-input docgen-admin-input-narrow"
                    value={editModalMaxAge}
                    onChange={(e) => setEditModalMaxAge(e.target.value)}
                    placeholder="—"
                  />
                </div>
              </div>
              <div className="docgen-admin-form-row">
                <span className="docgen-admin-options-label">Prompt fields</span>
                <div className="docgen-admin-checkbox-group">
                  {PROMPT_TAG_OPTIONS.map((opt) => (
                    <label key={opt.value} className="docgen-admin-checkbox-label">
                      <input type="checkbox" checked={editModalPromptTags.includes(opt.value)} onChange={() => toggleEditModalPromptTag(opt.value)} />
                      {opt.label}
                    </label>
                  ))}
                </div>
              </div>
              <div className="docgen-admin-form-actions docgen-admin-edit-modal-actions">
                <button type="button" className="docgen-admin-btn docgen-admin-btn-primary" disabled={editModalSaving} onClick={handleSaveEditModal}>
                  {editModalSaving ? 'Saving…' : 'Save'}
                </button>
                <button type="button" className="docgen-admin-btn docgen-admin-btn-secondary" onClick={closeEditModal}>Cancel</button>
              </div>
            </div>
          </div>
        </div>
      )}
      <TemplateActionsMenu
        menu={templateMenu}
        onClose={() => setTemplateMenu(null)}
        onEdit={openEditModal}
        onDownload={handleDownload}
        onToggleEnabled={handleSetEnabled}
        onDelete={handleDelete}
      />
    </div>
  );
}

export default DocGenTemplatesAdmin;
